import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public article API client. Used by /resources index, /resources/:slug and
 * /resources/topic/:cluster pages to fetch articles from the backend.
 *
 * Backend documents use snake_case; the site components use camelCase (e.g.
 * readMinutes). This helper normalises the shape once so downstream views
 * don't need to know about the storage format.
 */

export function normaliseArticle(doc) {
  if (!doc) return doc;
  return {
    ...doc,
    readMinutes: doc.read_minutes ?? doc.readMinutes ?? 6,
  };
}

export async function fetchArticles() {
  const res = await fetch(`${API}/api/articles`);
  if (!res.ok) throw new Error(`Failed to load articles (${res.status})`);
  const list = await res.json();
  return list.map(normaliseArticle);
}

export async function fetchArticle(slug) {
  const res = await fetch(`${API}/api/articles/${encodeURIComponent(slug)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to load article (${res.status})`);
  return normaliseArticle(await res.json());
}

export function useArticles() {
  const [state, setState] = useState({ articles: [], loading: true, error: '' });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const articles = await fetchArticles();
        if (!cancelled) setState({ articles, loading: false, error: '' });
      } catch (err) {
        if (!cancelled) setState({ articles: [], loading: false, error: err.message });
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return state;
}

export function useArticle(slug) {
  const [state, setState] = useState({ article: null, loading: true, error: '' });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const article = await fetchArticle(slug);
        if (!cancelled) setState({ article, loading: false, error: '' });
      } catch (err) {
        if (!cancelled) setState({ article: null, loading: false, error: err.message });
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);
  return state;
}
